import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { invalidateFor } from '@/lib/cache-registry';
import { DEFAULT_ROLE_CONFIGS, type RoleWidgetConfig } from '@/lib/widget-registry';

interface WidgetConfigRow {
  id: string;
  role: string;
  widgets: RoleWidgetConfig['widgets'];
  updated_at: string;
  updated_by: string | null;
}

const QUERY_KEY = ['widget-configs'];

/** Fetch all role widget configs from DB, falling back to defaults */
export function useWidgetConfigs() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<RoleWidgetConfig[]> => {
      const { data, error } = await supabase
        .from('widget_configs')
        .select('role, widgets')
        .order('role');

      if (error) throw error;

      if (!data || data.length === 0) {
        return DEFAULT_ROLE_CONFIGS;
      }

      return data.map((row) => ({
        role: row.role,
        widgets: row.widgets as RoleWidgetConfig['widgets'],
      }));
    },
    staleTime: 60_000,
  });
}

/** Fetch widget config for a single role (used by dashboard) */
export function useWidgetConfigForRole(role: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, role],
    queryFn: async (): Promise<RoleWidgetConfig['widgets']> => {
      const { data, error } = await supabase
        .from('widget_configs')
        .select('widgets')
        .eq('role', role)
        .maybeSingle();

      if (error) throw error;

      // VIB-708-followup: Fall tilbake til DEFAULT_ROLE_CONFIGS hvis raden
      // mangler ELLER er tom. Tidligere returnerte vi en tom array ved
      // tilstedeværende men tom DB-rad — det ga blanke dashboards. Tom
      // konfig er aldri intentional, så fallback gir bedre default-UX.
      const stored = (data?.widgets as RoleWidgetConfig['widgets'] | null) ?? [];
      if (stored.length > 0) return stored;

      const fallback = DEFAULT_ROLE_CONFIGS.find((c) => c.role === role);
      return fallback?.widgets ?? DEFAULT_ROLE_CONFIGS[0].widgets;
    },
    staleTime: 60_000,
  });
}

/** Save widget config for a role (upsert) */
export function useSaveWidgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RoleWidgetConfig | RoleWidgetConfig[]) => {
      const configs = Array.isArray(input) ? input : [input];
      const rows = configs.map((c) => ({
        role: c.role,
        widgets: c.widgets,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('widget_configs')
        .upsert(rows, { onConflict: 'role' });

      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(queryClient, 'widget_configs');
    },
  });
}
